import type { Locale } from '@/constants/i18n'
import type { AmenityKey } from '@/constants/amenities'

import { pl } from '@/i18n/plural'

const en = {
    meta: {
        description: "STAYVORA is a closed booking platform for private villas and apartments in Djerba, Zarzis, Hammamet, Tunis and Sousse."
    },
    common: {
        continue: "Continue",
        back: "Back",
        cancel: "Cancel",
        save: "Save changes",
        saving: "Saving…",
        sending: "Sending…",
        loading: "Loading…",
        close: "Close",
        or: "or",
        edit: "Edit",
        securityNote: "Protected by an automatic security check.",
        nights: pl({ one: "1 night", other: "{n} nights" }),
        adultsCount: pl({ one: "1 adult", other: "{n} adults" }),
        childrenCount: pl({ one: "1 child", other: "{n} children" }),
        guestsCount: pl({ one: "1 guest", other: "{n} guests" }),
        bedrooms: pl({ one: "1 bedroom", other: "{n} bedrooms" }),
        bathrooms: pl({ one: "1 bathroom", other: "{n} bathrooms" }),
        upToGuests: "Up to {n} guests"
    },
    errors: {
        required: "Sorry, please fill in this field.",
        invalidEmail: "Please enter a valid email address.",
        invalidName: "Please enter a valid name.",
        invalidNationality: "Please select your nationality.",
        phoneCountry: "Please select your international country code.",
        invalidPhone: "Please enter a valid phone number including the international country code.",
        passwordShort: "Your password must have at least 8 characters.",
        passwordWeak: "Your password must contain letters and numbers.",
        passwordMismatch: "The passwords do not match.",
        emailTaken: "An account with this email address already exists. Please sign in.",
        invalidCredentials: "The email address or the password is incorrect.",
        socialAccount: "This account signs in with {provider}. Please use the button “Continue with {provider}”.",
        rateLimited: "Too many attempts. Please wait a few minutes and try again.",
        captchaFailed: "The security check failed. Please try again.",
        securityPending: "The security check is still running. Please try again in a moment.",
        providerUnavailable: "Sign-in with {provider} is not available yet. Please register with your email address in the meantime.",
        oauthFailed: "Sign-in with {provider} could not be completed. Please try again.",
        invalidCode: "The security code is invalid or has expired.",
        codeAttempts: "Too many wrong entries. Please request a new code.",
        wrongPassword: "Your current password is incorrect.",
        invalidMtcn: "Please enter the 10-digit MTCN number.",
        mtcnMismatch: "The two MTCN numbers do not match.",
        screenshotRequired: "Please upload your payment screenshot.",
        screenshotType: "Please upload an image (JPG, PNG, WEBP) or a PDF file.",
        screenshotSize: "The file is too large (max. 8 MB).",
        termsRequired: "Please accept the booking terms to continue.",
        notAvailable: "Sorry, this villa has just been reserved for these dates. Please choose other dates.",
        tooManyGuests: "Sorry, this villa cannot accommodate that many guests.",
        invalidStay: "Please check your travel dates.",
        generic: "Sorry, something went wrong. Please try again.",
        network: "Connection problem. Please check your internet connection and try again."
    },
    nav: {
        home: "Search villas",
        profile: "My profile",
        bookings: "My bookings",
        privacy: "Privacy",
        about: "Who are we?",
        contact: "Contact details",
        logout: "Sign out",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        language: "Language",
        account: "My account"
    },
    footer: {
        tagline: "Book • Stay • Discover",
        rights: "All rights reserved.",
        terms: "Booking terms",
        privacy: "Privacy",
        contact: "Contact"
    },
    auth: {
        title: "Welcome to STAYVORA",
        subtitle: "Our booking platform is reserved for registered guests. Create your personal account or sign in to discover our villas and apartments in Tunisia.",
        tabRegister: "Create account",
        tabLogin: "Sign in",
        google: "Continue with Google",
        facebook: "Continue with Facebook",
        apple: "Continue with Apple",
        emailRegister: "Register with email",
        emailLogin: "Sign in with email",
        socialNote: "With Google, Facebook or Apple you sign in directly, without a separate STAYVORA password.",
        firstName: "First name",
        lastName: "Last name",
        nationality: "Nationality",
        selectNationality: "Select your nationality",
        phone: "Phone number",
        countryCode: "Country code",
        selectCountryCode: "Code",
        phonePlaceholder: "Mobile number",
        email: "Email address",
        password: "Password",
        passwordConfirm: "Repeat password",
        passwordHint: "At least 8 characters, including letters and numbers.",
        showPassword: "Show password",
        hidePassword: "Hide password",
        submitRegister: "Create my account",
        submitLogin: "Sign in",
        forgot: "Forgot password?",
        privacyPrefix: "By creating an account you accept our",
        privacyLink: "privacy policy",
        privacySuffix: ".",
        languageLabel: "Language"
    },
    onboarding: {
        title: "Complete your profile",
        subtitle: "Please complete your personal details. We need them to contact you about your bookings.",
        signedInWith: "Signed in with {provider}",
        submit: "Save and continue"
    },
    welcome: {
        chooseTitle: "Choose your language"
    },
    forgot: {
        title: "Reset your password",
        subtitle: "Enter the email address of your STAYVORA account. We will send you a one-time security code.",
        channel: "Where should we send the code?",
        byEmail: "By email",
        bySms: "By SMS to my phone number",
        send: "Send security code",
        codeTitle: "Enter your security code",
        codeText: "If an account exists for {email}, we have sent you a 6-digit security code. It is valid for 10 minutes and can only be used once.",
        code: "Security code",
        newPassword: "New password",
        confirmPassword: "Repeat new password",
        submit: "Save new password",
        resend: "Request a new code",
        socialHint: "Did you register with Google, Facebook or Apple? Then your password is managed by that provider. Simply sign in with the matching button.",
        backToLogin: "Back to sign in"
    },
    home: {
        eyebrow: "Private villas & apartments in Tunisia",
        title: "Your private villa in Tunisia",
        subtitle: "Selected villas and apartments in Djerba, Zarzis, Hammamet, Tunis and Sousse, booked personally with the STAYVORA team.",
        trust1: "Every booking personally confirmed",
        trust2: "Service in four languages",
        trust3: "Closed platform for registered guests",
        stepsTitle: "How booking with STAYVORA works",
        step1Title: "Search and choose",
        step1Text: "Choose your destination, travel dates and guests. You only see villas that are really free for your dates.",
        step2Title: "Pay by Western Union",
        step2Text: "Send the total amount as a Western Union cash transfer and confirm your payment with the MTCN number.",
        step3Title: "Personal confirmation",
        step3Text: "Our team checks your payment and sends your booking confirmation with booking number by SMS and email."
    },
    search: {
        destination: "Destination",
        destinationPlaceholder: "Where would you like to stay?",
        typeCity: "Type a city…",
        ourDestinations: "Our destinations",
        noMatches: "No match. Please choose one of our five destinations.",
        checkIn: "Check-in",
        checkOut: "Check-out",
        selectDate: "Select a date",
        guests: "Guests",
        adults: "Adults",
        adultsHint: "From 13 years",
        children: "Children",
        childrenHint: "0 to 12 years",
        increase: "Add one",
        decrease: "Remove one",
        button: "Search now",
        invalidCity: "Sorry, we only offer stays in Djerba, Zarzis, Hammamet, Tunis and Sousse.",
        checkoutAfter: "Sorry, the check-out date must be after the check-in date.",
        pastDate: "Sorry, this date is in the past.",
        prevMonth: "Previous month",
        nextMonth: "Next month",
        done: "Done"
    },
    results: {
        title: "Villas in {city}",
        count: pl({ zero: "No villa available", one: "1 villa available", other: "{n} villas available" }),
        none: "Sorry, no villa is available for your dates.",
        noneHint: "Please try other travel dates or another destination.",
        totalFor: "Total for {nights}",
        perNight: "approx. {price} per night",
        extrasIncluded: "incl. {amount} extra costs",
        details: "Show more details",
        invalidSearch: "Please check your search details.",
        photoPrev: "Previous photo",
        photoNext: "Next photo",
        noPhoto: "Photos coming soon"
    },
    villa: {
        back: "Back to results",
        about: "About this villa",
        amenities: "Amenities",
        address: "Address",
        yourStay: "Your stay",
        nightsLine: "{nights}, average {price} per night",
        extras: "Extra costs",
        total: "Total price",
        bookNow: "Book now",
        chooseDates: "Choose your travel dates to see the total price and book.",
        checkAvailability: "Check availability",
        unavailable: "Sorry, this villa is not available for the selected dates.",
        tooManyGuests: "This villa accommodates up to {n} guests.",
        descriptionMissing: "A detailed description will follow soon."
    },
    extraTypes: {
        per_stay: "per stay",
        per_night: "per night",
        per_guest: "per guest",
        per_guest_night: "per guest and night"
    },
    booking: {
        stepSummary: "Overview",
        stepPayment: "Payment",
        stepDetails: "Confirmation",
        stepDone: "Received",
        summaryTitle: "Check your booking",
        villa: "Villa",
        location: "Location",
        checkIn: "Check-in",
        checkOut: "Check-out",
        duration: "Duration",
        guests: "Guests",
        total: "Total price",
        priceDetails: "Price details",
        termsPrefix: "I have read and accept the",
        termsLink: "booking terms",
        termsSuffix: ".",
        toPayment: "Continue to payment",
        amountTitle: "Amount to send",
        amountHint: "Please send exactly this amount to the recipient below.",
        recipientTitle: "Your payment recipient",
        officialName: "Official name",
        nameForPayment: "Name for the payment",
        readyToCopy: "Ready to copy",
        copyName: "Copy name",
        copied: "Name copied! You can now paste it at Western Union.",
        copyFailed: "Copying is not available on this device. Please enter the name exactly as shown.",
        thanksTitle: "Thank you for your trust!",
        thanksText: "To complete your booking, please copy the name shown above first. Then click the link to the official Western Union website.",
        wuButton: "Official Western Union website – click here",
        wuNote: "Opens in a new tab. Your STAYVORA page stays open.",
        checkNote: "Please check the recipient and the amount before you pay.",
        guideTitle: "Payment guide – Western Union",
        guide: [
            "Open the official Western Union website or app.",
            "Sign in or create an account.",
            "Select Tunisia as the destination country.",
            "Enter the booking amount.",
            "Choose “Cash pickup”.",
            "Enter the recipient name shown above exactly.",
            "Check all details and confirm the transfer.",
            "Keep your transfer receipt and the MTCN number."
        ],
        warning: "Please do not close this STAYVORA page while you complete your transfer.",
        paymentDone: "PAYMENT COMPLETED",
        formTitle: "Confirm your payment",
        formSubtitle: "Please enter the details of your Western Union transfer. All fields are required.",
        mtcn: "MTCN number",
        mtcnHint: "The 10-digit transfer number on your Western Union receipt.",
        mtcnRepeat: "Repeat MTCN number",
        screenshot: "Original payment screenshot",
        screenshotHint: "Screenshot or photo of your Western Union receipt (JPG, PNG, WEBP or PDF, max. 8 MB).",
        chooseFile: "Choose file",
        changeFile: "Choose another file",
        contactTitle: "Your contact details",
        submit: "Submit payment details",
        doneTitle: "Thank you! We have received your payment details.",
        doneWithin: "Our team is now checking your payment. During our opening hours ({from} to {to}) you will receive an answer within one hour at the latest.",
        doneOutside: "We are currently outside our opening hours ({from} to {to}). Our team will check your payment during the next opening hours.",
        doneNote: "Please note: this confirmation of receipt is not yet a payment confirmation. As soon as we have verified your payment, you will receive your booking confirmation with your booking number by SMS and email.",
        doneMessages: "We have also sent you a confirmation of receipt by SMS and email.",
        backHome: "Back to home",
        leaveWarning: "Your booking is not complete yet. Do you really want to leave this page?",
        unavailableTitle: "This stay is not available",
        backToSearch: "Back to search"
    },
    profile: {
        title: "My profile",
        subtitle: "Your personal details at a glance. You can change them at any time.",
        personal: "Personal details",
        signInMethod: "Sign-in method",
        methods: {
            email: "Email and password",
            google: "Google",
            facebook: "Facebook",
            apple: "Apple"
        },
        emailFixed: "Your email address is used for signing in and cannot be changed here.",
        language: "Language",
        languageHint: "Pages, emails and SMS are shown in this language.",
        saved: "Your details have been saved.",
        passwordTitle: "Change password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmPassword: "Repeat new password",
        changePassword: "Change password",
        passwordSaved: "Your password has been changed.",
        passwordManaged: "You sign in with {provider}. Your password is managed by {provider}.",
        memberSince: "Member since {date}"
    },
    bookings: {
        title: "My bookings",
        subtitle: "Here you can find the bookings that our team has released for you.",
        empty: "No bookings to show yet.",
        emptyHint: "As soon as our team has released your booking, it will appear here.",
        reference: "Booking number",
        referencePending: "Assigned after confirmation",
        submittedOn: "Submitted on {date}",
        status: {
            new: "Payment received",
            reviewing: "Under review",
            confirmed: "Confirmed",
            rejected: "Not confirmed"
        },
        searchCta: "Search villas"
    },
    contact: {
        title: "Contact details",
        subtitle: "We are happy to help you, in English, German, French and Arabic.",
        phone: "Phone",
        email: "Email",
        person: "Contact person",
        address: "Business address",
        hours: "Opening hours",
        hoursValue: "Daily from {from} to {to} (Tunisia time)",
        landmark: "Next to the {name} mosque",
        country: "Tunisia"
    },
    notFound: {
        title: "Page not found",
        text: "Sorry, we could not find this page.",
        back: "Back to home"
    },
    calendar: {
        monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        monthsLong: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        weekdays: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
        datePattern: "{d} {mon} {y}"
    },
    amenities: {
        private_pool: "Private pool",
        pool: "Pool",
        sea_view: "Sea view",
        beach_access: "Close to the beach",
        wifi: "Wi-Fi",
        air_conditioning: "Air conditioning",
        heating: "Heating",
        kitchen: "Fully equipped kitchen",
        washing_machine: "Washing machine",
        tv: "TV",
        parking: "Parking",
        garden: "Garden",
        terrace: "Terrace",
        bbq: "Barbecue",
        jacuzzi: "Jacuzzi",
        family_friendly: "Family friendly",
        pets_allowed: "Pets allowed",
        security: "Security service",
        cleaning: "Cleaning service",
        enclosed: "Enclosed property"
    } as Record<AmenityKey, string>
}

export type Messages = typeof en

const de: Messages = {
    meta: {
        description: "STAYVORA ist eine geschlossene Buchungsplattform für private Villen und Wohnungen in Djerba, Zarzis, Hammamet, Tunis und Sousse."
    },
    common: {
        continue: "Weiter",
        back: "Zurück",
        cancel: "Abbrechen",
        save: "Änderungen speichern",
        saving: "Wird gespeichert…",
        sending: "Wird gesendet…",
        loading: "Wird geladen…",
        close: "Schließen",
        or: "oder",
        edit: "Bearbeiten",
        securityNote: "Geschützt durch eine automatische Sicherheitsprüfung.",
        nights: pl({ one: "1 Nacht", other: "{n} Nächte" }),
        adultsCount: pl({ one: "1 Erwachsener", other: "{n} Erwachsene" }),
        childrenCount: pl({ one: "1 Kind", other: "{n} Kinder" }),
        guestsCount: pl({ one: "1 Gast", other: "{n} Gäste" }),
        bedrooms: pl({ one: "1 Schlafzimmer", other: "{n} Schlafzimmer" }),
        bathrooms: pl({ one: "1 Badezimmer", other: "{n} Badezimmer" }),
        upToGuests: "Bis zu {n} Gäste"
    },
    errors: {
        required: "Sorry, bitte füllen Sie dieses Feld noch aus.",
        invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
        invalidName: "Bitte geben Sie einen gültigen Namen ein.",
        invalidNationality: "Bitte wählen Sie Ihre Nationalität aus.",
        phoneCountry: "Bitte wählen Sie Ihre internationale Ländervorwahl aus.",
        invalidPhone: "Bitte geben Sie eine gültige Telefonnummer mit internationaler Ländervorwahl ein.",
        passwordShort: "Ihr Passwort muss mindestens 8 Zeichen haben.",
        passwordWeak: "Ihr Passwort muss Buchstaben und Zahlen enthalten.",
        passwordMismatch: "Die Passwörter stimmen nicht überein.",
        emailTaken: "Mit dieser E-Mail-Adresse besteht bereits ein Konto. Bitte melden Sie sich an.",
        invalidCredentials: "Die E-Mail-Adresse oder das Passwort ist falsch.",
        socialAccount: "Dieses Konto meldet sich über {provider} an. Bitte nutzen Sie den Button „Weiter mit {provider}“.",
        rateLimited: "Zu viele Versuche. Bitte warten Sie einige Minuten und versuchen Sie es erneut.",
        captchaFailed: "Die Sicherheitsprüfung ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
        securityPending: "Die Sicherheitsprüfung läuft noch. Bitte versuchen Sie es gleich noch einmal.",
        providerUnavailable: "Die Anmeldung mit {provider} ist noch nicht verfügbar. Bitte registrieren Sie sich in der Zwischenzeit mit Ihrer E-Mail-Adresse.",
        oauthFailed: "Die Anmeldung mit {provider} konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.",
        invalidCode: "Der Sicherheitscode ist ungültig oder abgelaufen.",
        codeAttempts: "Zu viele falsche Eingaben. Bitte fordern Sie einen neuen Code an.",
        wrongPassword: "Ihr aktuelles Passwort ist nicht korrekt.",
        invalidMtcn: "Bitte geben Sie die 10-stellige MTCN-Nummer ein.",
        mtcnMismatch: "Die beiden MTCN-Nummern stimmen nicht überein.",
        screenshotRequired: "Bitte laden Sie Ihren Zahlungsscreenshot hoch.",
        screenshotType: "Bitte laden Sie ein Bild (JPG, PNG, WEBP) oder eine PDF-Datei hoch.",
        screenshotSize: "Die Datei ist zu groß (max. 8 MB).",
        termsRequired: "Bitte akzeptieren Sie die Buchungsbedingungen, um fortzufahren.",
        notAvailable: "Sorry, diese Villa wurde für diese Daten gerade reserviert. Bitte wählen Sie andere Daten.",
        tooManyGuests: "Sorry, diese Villa bietet nicht genug Platz für so viele Gäste.",
        invalidStay: "Bitte prüfen Sie Ihre Reisedaten.",
        generic: "Sorry, etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
        network: "Verbindungsproblem. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut."
    },
    nav: {
        home: "Villen suchen",
        profile: "Mein Profil",
        bookings: "Meine Buchungen",
        privacy: "Datenschutz",
        about: "Wer sind wir?",
        contact: "Kontaktdaten",
        logout: "Abmelden",
        openMenu: "Menü öffnen",
        closeMenu: "Menü schließen",
        language: "Sprache",
        account: "Mein Konto"
    },
    footer: {
        tagline: "Buchen • Wohnen • Entdecken",
        rights: "Alle Rechte vorbehalten.",
        terms: "Buchungsbedingungen",
        privacy: "Datenschutz",
        contact: "Kontakt"
    },
    auth: {
        title: "Willkommen bei STAYVORA",
        subtitle: "Unsere Buchungsplattform ist registrierten Gästen vorbehalten. Erstellen Sie Ihr persönliches Konto oder melden Sie sich an, um unsere Villen und Wohnungen in Tunesien zu entdecken.",
        tabRegister: "Konto erstellen",
        tabLogin: "Anmelden",
        google: "Weiter mit Google",
        facebook: "Weiter mit Facebook",
        apple: "Weiter mit Apple",
        emailRegister: "Mit E-Mail registrieren",
        emailLogin: "Mit E-Mail anmelden",
        socialNote: "Mit Google, Facebook oder Apple melden Sie sich direkt an, ohne separates STAYVORA-Passwort.",
        firstName: "Vorname",
        lastName: "Nachname",
        nationality: "Nationalität",
        selectNationality: "Nationalität auswählen",
        phone: "Telefonnummer",
        countryCode: "Ländervorwahl",
        selectCountryCode: "Vorwahl",
        phonePlaceholder: "Mobilnummer",
        email: "E-Mail-Adresse",
        password: "Passwort",
        passwordConfirm: "Passwort wiederholen",
        passwordHint: "Mindestens 8 Zeichen, mit Buchstaben und Zahlen.",
        showPassword: "Passwort anzeigen",
        hidePassword: "Passwort verbergen",
        submitRegister: "Mein Konto erstellen",
        submitLogin: "Anmelden",
        forgot: "Passwort vergessen?",
        privacyPrefix: "Mit der Registrierung akzeptieren Sie unsere",
        privacyLink: "Datenschutzerklärung",
        privacySuffix: ".",
        languageLabel: "Sprache"
    },
    onboarding: {
        title: "Profil vervollständigen",
        subtitle: "Bitte ergänzen Sie Ihre persönlichen Angaben. Wir benötigen sie, um Sie zu Ihren Buchungen zu kontaktieren.",
        signedInWith: "Angemeldet mit {provider}",
        submit: "Speichern und weiter"
    },
    welcome: {
        chooseTitle: "Wählen Sie Ihre Sprache"
    },
    forgot: {
        title: "Passwort zurücksetzen",
        subtitle: "Geben Sie die E-Mail-Adresse Ihres STAYVORA-Kontos ein. Wir senden Ihnen einen einmaligen Sicherheitscode.",
        channel: "Wohin sollen wir den Code senden?",
        byEmail: "Per E-Mail",
        bySms: "Per SMS an meine Telefonnummer",
        send: "Sicherheitscode senden",
        codeTitle: "Sicherheitscode eingeben",
        codeText: "Falls ein Konto für {email} besteht, haben wir Ihnen einen 6-stelligen Sicherheitscode gesendet. Er ist 10 Minuten gültig und nur einmal verwendbar.",
        code: "Sicherheitscode",
        newPassword: "Neues Passwort",
        confirmPassword: "Neues Passwort wiederholen",
        submit: "Neues Passwort speichern",
        resend: "Neuen Code anfordern",
        socialHint: "Sie haben sich mit Google, Facebook oder Apple registriert? Dann verwaltet der jeweilige Anbieter Ihr Passwort. Melden Sie sich einfach mit dem passenden Button an.",
        backToLogin: "Zurück zur Anmeldung"
    },
    home: {
        eyebrow: "Private Villen & Wohnungen in Tunesien",
        title: "Ihre private Villa in Tunesien",
        subtitle: "Ausgewählte Villen und Wohnungen in Djerba, Zarzis, Hammamet, Tunis und Sousse, persönlich gebucht mit dem STAYVORA-Team.",
        trust1: "Jede Buchung persönlich bestätigt",
        trust2: "Service in vier Sprachen",
        trust3: "Geschlossene Plattform für registrierte Gäste",
        stepsTitle: "So funktioniert die Buchung mit STAYVORA",
        step1Title: "Suchen und auswählen",
        step1Text: "Wählen Sie Reiseziel, Reisedaten und Gäste. Sie sehen nur Villen, die für Ihren Zeitraum wirklich frei sind.",
        step2Title: "Per Western Union bezahlen",
        step2Text: "Senden Sie den Gesamtbetrag per Western-Union-Barüberweisung und bestätigen Sie Ihre Zahlung mit der MTCN-Nummer.",
        step3Title: "Persönliche Bestätigung",
        step3Text: "Unser Team prüft Ihre Zahlung und sendet Ihnen die Buchungsbestätigung mit Buchungsnummer per SMS und E-Mail."
    },
    search: {
        destination: "Reiseziel",
        destinationPlaceholder: "Wohin möchten Sie reisen?",
        typeCity: "Stadt eingeben…",
        ourDestinations: "Unsere Reiseziele",
        noMatches: "Kein Treffer. Bitte wählen Sie eines unserer fünf Reiseziele.",
        checkIn: "Check-in",
        checkOut: "Check-out",
        selectDate: "Datum wählen",
        guests: "Gäste",
        adults: "Erwachsene",
        adultsHint: "Ab 13 Jahren",
        children: "Kinder",
        childrenHint: "0 bis 12 Jahre",
        increase: "Einen hinzufügen",
        decrease: "Einen entfernen",
        button: "Jetzt suchen",
        invalidCity: "Sorry, wir bieten Unterkünfte nur in Djerba, Zarzis, Hammamet, Tunis und Sousse an.",
        checkoutAfter: "Sorry, das Abreisedatum muss nach dem Anreisedatum liegen.",
        pastDate: "Sorry, dieses Datum liegt in der Vergangenheit.",
        prevMonth: "Vorheriger Monat",
        nextMonth: "Nächster Monat",
        done: "Fertig"
    },
    results: {
        title: "Villen in {city}",
        count: pl({ zero: "Keine Villa verfügbar", one: "1 Villa verfügbar", other: "{n} Villen verfügbar" }),
        none: "Sorry, für Ihre Reisedaten ist keine Villa verfügbar.",
        noneHint: "Bitte versuchen Sie andere Reisedaten oder ein anderes Reiseziel.",
        totalFor: "Gesamtpreis für {nights}",
        perNight: "ca. {price} pro Nacht",
        extrasIncluded: "inkl. {amount} Zusatzkosten",
        details: "Mehr Details anzeigen",
        invalidSearch: "Bitte prüfen Sie Ihre Suchangaben.",
        photoPrev: "Vorheriges Foto",
        photoNext: "Nächstes Foto",
        noPhoto: "Fotos folgen in Kürze"
    },
    villa: {
        back: "Zurück zu den Ergebnissen",
        about: "Über diese Villa",
        amenities: "Ausstattung",
        address: "Adresse",
        yourStay: "Ihr Aufenthalt",
        nightsLine: "{nights}, durchschnittlich {price} pro Nacht",
        extras: "Zusatzkosten",
        total: "Gesamtpreis",
        bookNow: "Jetzt buchen",
        chooseDates: "Wählen Sie Ihre Reisedaten, um den Gesamtpreis zu sehen und zu buchen.",
        checkAvailability: "Verfügbarkeit prüfen",
        unavailable: "Sorry, diese Villa ist für die gewählten Daten nicht verfügbar.",
        tooManyGuests: "Diese Villa bietet Platz für bis zu {n} Gäste.",
        descriptionMissing: "Eine ausführliche Beschreibung folgt in Kürze."
    },
    extraTypes: {
        per_stay: "pro Aufenthalt",
        per_night: "pro Nacht",
        per_guest: "pro Gast",
        per_guest_night: "pro Gast und Nacht"
    },
    booking: {
        stepSummary: "Übersicht",
        stepPayment: "Zahlung",
        stepDetails: "Bestätigung",
        stepDone: "Eingegangen",
        summaryTitle: "Prüfen Sie Ihre Buchung",
        villa: "Villa",
        location: "Standort",
        checkIn: "Anreise",
        checkOut: "Abreise",
        duration: "Dauer",
        guests: "Gäste",
        total: "Gesamtpreis",
        priceDetails: "Preisdetails",
        termsPrefix: "Ich habe die",
        termsLink: "Buchungsbedingungen",
        termsSuffix: "gelesen und akzeptiere sie.",
        toPayment: "Weiter zur Zahlung",
        amountTitle: "Zu überweisender Betrag",
        amountHint: "Bitte senden Sie genau diesen Betrag an den unten genannten Empfänger.",
        recipientTitle: "Ihr Zahlungsempfänger",
        officialName: "Offizieller Name",
        nameForPayment: "Name für die Zahlung",
        readyToCopy: "Kopierbereit",
        copyName: "Namen kopieren",
        copied: "Name kopiert! Sie können ihn jetzt bei Western Union einfügen.",
        copyFailed: "Kopieren ist auf diesem Gerät nicht möglich. Bitte geben Sie den Namen exakt wie angezeigt ein.",
        thanksTitle: "Vielen Dank für Ihr Vertrauen!",
        thanksText: "Damit Sie Ihre Buchung fertigstellen können, kopieren Sie bitte zuerst den oben angezeigten Namen. Klicken Sie anschließend auf den Link zur offiziellen Western-Union-Webseite.",
        wuButton: "Offizielle Western-Union-Webseite – hier klicken",
        wuNote: "Öffnet sich in einem neuen Tab. Ihre STAYVORA-Seite bleibt geöffnet.",
        checkNote: "Bitte prüfen Sie vor einer Zahlung den Empfänger und den Zahlungsbetrag.",
        guideTitle: "Zahlungsanleitung – Western Union",
        guide: [
            "Öffnen Sie die offizielle Western-Union-Webseite oder die App.",
            "Melden Sie sich an oder erstellen Sie ein Konto.",
            "Wählen Sie Tunesien als Zielland.",
            "Geben Sie den Buchungsbetrag ein.",
            "Wählen Sie „Barabholung“ (Cash Pickup).",
            "Geben Sie den oben angezeigten Empfängernamen exakt ein.",
            "Prüfen Sie alle Angaben und bestätigen Sie die Überweisung.",
            "Bewahren Sie die Überweisungsbestätigung und die MTCN-Nummer auf."
        ],
        warning: "Bitte schließen Sie diese STAYVORA-Seite nicht, während Sie Ihre Überweisung durchführen.",
        paymentDone: "ZAHLUNG ABGESCHLOSSEN",
        formTitle: "Zahlung bestätigen",
        formSubtitle: "Bitte geben Sie die Angaben zu Ihrer Western-Union-Überweisung ein. Alle Felder sind Pflichtfelder.",
        mtcn: "MTCN-Nummer",
        mtcnHint: "Die 10-stellige Überweisungsnummer auf Ihrem Western-Union-Beleg.",
        mtcnRepeat: "MTCN-Nummer erneut eingeben",
        screenshot: "Original-Zahlungsscreenshot",
        screenshotHint: "Screenshot oder Foto Ihres Western-Union-Belegs (JPG, PNG, WEBP oder PDF, max. 8 MB).",
        chooseFile: "Datei auswählen",
        changeFile: "Andere Datei auswählen",
        contactTitle: "Ihre Kontaktdaten",
        submit: "Zahlungsangaben senden",
        doneTitle: "Vielen Dank! Wir haben Ihre Zahlungsangaben erhalten.",
        doneWithin: "Unser Team prüft jetzt Ihre Zahlung. Während unserer Öffnungszeiten ({from} bis {to} Uhr) erhalten Sie innerhalb von maximal einer Stunde eine Rückmeldung.",
        doneOutside: "Wir befinden uns gerade außerhalb unserer Öffnungszeiten ({from} bis {to} Uhr). Unser Team prüft Ihre Zahlung während der nächsten Öffnungszeiten.",
        doneNote: "Bitte beachten Sie: Diese Eingangsbestätigung ist noch keine Zahlungsbestätigung. Sobald wir Ihre Zahlung geprüft haben, erhalten Sie Ihre Buchungsbestätigung mit Buchungsnummer per SMS und E-Mail.",
        doneMessages: "Wir haben Ihnen zusätzlich eine Eingangsbestätigung per SMS und E-Mail gesendet.",
        backHome: "Zur Startseite",
        leaveWarning: "Ihre Buchung ist noch nicht abgeschlossen. Möchten Sie diese Seite wirklich verlassen?",
        unavailableTitle: "Dieser Aufenthalt ist nicht verfügbar",
        backToSearch: "Zurück zur Suche"
    },
    profile: {
        title: "Mein Profil",
        subtitle: "Ihre persönlichen Daten im Überblick. Sie können sie jederzeit ändern.",
        personal: "Persönliche Daten",
        signInMethod: "Anmeldemethode",
        methods: {
            email: "E-Mail und Passwort",
            google: "Google",
            facebook: "Facebook",
            apple: "Apple"
        },
        emailFixed: "Ihre E-Mail-Adresse dient zur Anmeldung und kann hier nicht geändert werden.",
        language: "Sprache",
        languageHint: "Seiten, E-Mails und SMS erhalten Sie in dieser Sprache.",
        saved: "Ihre Angaben wurden gespeichert.",
        passwordTitle: "Passwort ändern",
        currentPassword: "Aktuelles Passwort",
        newPassword: "Neues Passwort",
        confirmPassword: "Neues Passwort wiederholen",
        changePassword: "Passwort ändern",
        passwordSaved: "Ihr Passwort wurde geändert.",
        passwordManaged: "Sie melden sich mit {provider} an. Ihr Passwort wird von {provider} verwaltet.",
        memberSince: "Mitglied seit {date}"
    },
    bookings: {
        title: "Meine Buchungen",
        subtitle: "Hier finden Sie die Buchungen, die unser Team für Sie freigegeben hat.",
        empty: "Noch keine Buchungen vorhanden.",
        emptyHint: "Sobald unser Team Ihre Buchung freigegeben hat, erscheint sie hier.",
        reference: "Buchungsnummer",
        referencePending: "Wird nach der Bestätigung vergeben",
        submittedOn: "Eingereicht am {date}",
        status: {
            new: "Zahlung eingegangen",
            reviewing: "Wird überprüft",
            confirmed: "Bestätigt",
            rejected: "Nicht bestätigt"
        },
        searchCta: "Villen suchen"
    },
    contact: {
        title: "Kontaktdaten",
        subtitle: "Wir helfen Ihnen gern, auf Deutsch, Englisch, Französisch und Arabisch.",
        phone: "Telefon",
        email: "E-Mail",
        person: "Ansprechpartner",
        address: "Geschäftsadresse",
        hours: "Öffnungszeiten",
        hoursValue: "Täglich von {from} bis {to} Uhr (tunesische Zeit)",
        landmark: "Neben der Moschee {name}",
        country: "Tunesien"
    },
    notFound: {
        title: "Seite nicht gefunden",
        text: "Sorry, diese Seite konnten wir nicht finden.",
        back: "Zur Startseite"
    },
    calendar: {
        monthsShort: ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sept.", "Okt.", "Nov.", "Dez."],
        monthsLong: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
        weekdays: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
        datePattern: "{d}. {mon} {y}"
    },
    amenities: {
        private_pool: "Privater Pool",
        pool: "Pool",
        sea_view: "Meerblick",
        beach_access: "Strandnähe",
        wifi: "WLAN",
        air_conditioning: "Klimaanlage",
        heating: "Heizung",
        kitchen: "Voll ausgestattete Küche",
        washing_machine: "Waschmaschine",
        tv: "Fernseher",
        parking: "Parkplatz",
        garden: "Garten",
        terrace: "Terrasse",
        bbq: "Grill",
        jacuzzi: "Whirlpool",
        family_friendly: "Familienfreundlich",
        pets_allowed: "Haustiere erlaubt",
        security: "Sicherheitsdienst",
        cleaning: "Reinigungsservice",
        enclosed: "Eingezäuntes Grundstück"
    }
}

const fr: Messages = {
    meta: {
        description: "STAYVORA est une plateforme de réservation privée pour des villas et appartements à Djerba, Zarzis, Hammamet, Tunis et Sousse."
    },
    common: {
        continue: "Continuer",
        back: "Retour",
        cancel: "Annuler",
        save: "Enregistrer les modifications",
        saving: "Enregistrement…",
        sending: "Envoi en cours…",
        loading: "Chargement…",
        close: "Fermer",
        or: "ou",
        edit: "Modifier",
        securityNote: "Protégé par une vérification de sécurité automatique.",
        nights: pl({ one: "{n} nuit", other: "{n} nuits" }),
        adultsCount: pl({ one: "{n} adulte", other: "{n} adultes" }),
        childrenCount: pl({ one: "{n} enfant", other: "{n} enfants" }),
        guestsCount: pl({ one: "{n} voyageur", other: "{n} voyageurs" }),
        bedrooms: pl({ one: "{n} chambre", other: "{n} chambres" }),
        bathrooms: pl({ one: "{n} salle de bain", other: "{n} salles de bain" }),
        upToGuests: "Jusqu'à {n} voyageurs"
    },
    errors: {
        required: "Désolé, veuillez remplir ce champ.",
        invalidEmail: "Veuillez saisir une adresse e-mail valide.",
        invalidName: "Veuillez saisir un nom valide.",
        invalidNationality: "Veuillez sélectionner votre nationalité.",
        phoneCountry: "Veuillez sélectionner votre indicatif international.",
        invalidPhone: "Veuillez saisir un numéro de téléphone valide avec l'indicatif international.",
        passwordShort: "Votre mot de passe doit comporter au moins 8 caractères.",
        passwordWeak: "Votre mot de passe doit contenir des lettres et des chiffres.",
        passwordMismatch: "Les mots de passe ne correspondent pas.",
        emailTaken: "Un compte existe déjà avec cette adresse e-mail. Veuillez vous connecter.",
        invalidCredentials: "L'adresse e-mail ou le mot de passe est incorrect.",
        socialAccount: "Ce compte se connecte avec {provider}. Veuillez utiliser le bouton « Continuer avec {provider} ».",
        rateLimited: "Trop de tentatives. Veuillez patienter quelques minutes puis réessayer.",
        captchaFailed: "La vérification de sécurité a échoué. Veuillez réessayer.",
        securityPending: "La vérification de sécurité est en cours. Veuillez réessayer dans un instant.",
        providerUnavailable: "La connexion avec {provider} n'est pas encore disponible. Inscrivez-vous en attendant avec votre adresse e-mail.",
        oauthFailed: "La connexion avec {provider} n'a pas pu aboutir. Veuillez réessayer.",
        invalidCode: "Le code de sécurité est invalide ou a expiré.",
        codeAttempts: "Trop de saisies erronées. Veuillez demander un nouveau code.",
        wrongPassword: "Votre mot de passe actuel est incorrect.",
        invalidMtcn: "Veuillez saisir le numéro MTCN à 10 chiffres.",
        mtcnMismatch: "Les deux numéros MTCN ne correspondent pas.",
        screenshotRequired: "Veuillez télécharger la capture d'écran de votre paiement.",
        screenshotType: "Veuillez télécharger une image (JPG, PNG, WEBP) ou un fichier PDF.",
        screenshotSize: "Le fichier est trop volumineux (8 Mo maximum).",
        termsRequired: "Veuillez accepter les conditions de réservation pour continuer.",
        notAvailable: "Désolé, cette villa vient d'être réservée pour ces dates. Veuillez choisir d'autres dates.",
        tooManyGuests: "Désolé, cette villa ne peut pas accueillir autant de voyageurs.",
        invalidStay: "Veuillez vérifier vos dates de séjour.",
        generic: "Désolé, une erreur est survenue. Veuillez réessayer.",
        network: "Problème de connexion. Veuillez vérifier votre connexion Internet et réessayer."
    },
    nav: {
        home: "Rechercher une villa",
        profile: "Mon profil",
        bookings: "Mes réservations",
        privacy: "Confidentialité",
        about: "Qui sommes-nous ?",
        contact: "Coordonnées",
        logout: "Se déconnecter",
        openMenu: "Ouvrir le menu",
        closeMenu: "Fermer le menu",
        language: "Langue",
        account: "Mon compte"
    },
    footer: {
        tagline: "Réserver • Séjourner • Découvrir",
        rights: "Tous droits réservés.",
        terms: "Conditions de réservation",
        privacy: "Confidentialité",
        contact: "Contact"
    },
    auth: {
        title: "Bienvenue chez STAYVORA",
        subtitle: "Notre plateforme de réservation est réservée aux clients inscrits. Créez votre compte personnel ou connectez-vous pour découvrir nos villas et appartements en Tunisie.",
        tabRegister: "Créer un compte",
        tabLogin: "Se connecter",
        google: "Continuer avec Google",
        facebook: "Continuer avec Facebook",
        apple: "Continuer avec Apple",
        emailRegister: "S'inscrire avec un e-mail",
        emailLogin: "Se connecter avec un e-mail",
        socialNote: "Avec Google, Facebook ou Apple, vous vous connectez directement, sans mot de passe STAYVORA séparé.",
        firstName: "Prénom",
        lastName: "Nom",
        nationality: "Nationalité",
        selectNationality: "Choisissez votre nationalité",
        phone: "Numéro de téléphone",
        countryCode: "Indicatif",
        selectCountryCode: "Indicatif",
        phonePlaceholder: "Numéro de mobile",
        email: "Adresse e-mail",
        password: "Mot de passe",
        passwordConfirm: "Répéter le mot de passe",
        passwordHint: "Au moins 8 caractères, avec des lettres et des chiffres.",
        showPassword: "Afficher le mot de passe",
        hidePassword: "Masquer le mot de passe",
        submitRegister: "Créer mon compte",
        submitLogin: "Se connecter",
        forgot: "Mot de passe oublié ?",
        privacyPrefix: "En créant un compte, vous acceptez notre",
        privacyLink: "politique de confidentialité",
        privacySuffix: ".",
        languageLabel: "Langue"
    },
    onboarding: {
        title: "Complétez votre profil",
        subtitle: "Veuillez compléter vos informations personnelles. Nous en avons besoin pour vous contacter au sujet de vos réservations.",
        signedInWith: "Connecté avec {provider}",
        submit: "Enregistrer et continuer"
    },
    welcome: {
        chooseTitle: "Choisissez votre langue"
    },
    forgot: {
        title: "Réinitialiser le mot de passe",
        subtitle: "Saisissez l'adresse e-mail de votre compte STAYVORA. Nous vous enverrons un code de sécurité à usage unique.",
        channel: "Où devons-nous envoyer le code ?",
        byEmail: "Par e-mail",
        bySms: "Par SMS sur mon numéro de téléphone",
        send: "Envoyer le code de sécurité",
        codeTitle: "Saisissez votre code de sécurité",
        codeText: "Si un compte existe pour {email}, nous vous avons envoyé un code de sécurité à 6 chiffres. Il est valable 10 minutes et ne peut être utilisé qu'une seule fois.",
        code: "Code de sécurité",
        newPassword: "Nouveau mot de passe",
        confirmPassword: "Répéter le nouveau mot de passe",
        submit: "Enregistrer le nouveau mot de passe",
        resend: "Demander un nouveau code",
        socialHint: "Vous vous êtes inscrit avec Google, Facebook ou Apple ? Votre mot de passe est alors géré par ce fournisseur. Connectez-vous simplement avec le bouton correspondant.",
        backToLogin: "Retour à la connexion"
    },
    home: {
        eyebrow: "Villas et appartements privés en Tunisie",
        title: "Votre villa privée en Tunisie",
        subtitle: "Des villas et appartements sélectionnés à Djerba, Zarzis, Hammamet, Tunis et Sousse, réservés personnellement avec l'équipe STAYVORA.",
        trust1: "Chaque réservation confirmée personnellement",
        trust2: "Un service en quatre langues",
        trust3: "Plateforme privée pour clients inscrits",
        stepsTitle: "Comment réserver avec STAYVORA",
        step1Title: "Rechercher et choisir",
        step1Text: "Choisissez votre destination, vos dates et le nombre de voyageurs. Vous ne voyez que les villas réellement libres à vos dates.",
        step2Title: "Payer par Western Union",
        step2Text: "Envoyez le montant total par transfert en espèces Western Union et confirmez votre paiement avec le numéro MTCN.",
        step3Title: "Confirmation personnelle",
        step3Text: "Notre équipe vérifie votre paiement et vous envoie la confirmation de réservation avec votre numéro de réservation par SMS et e-mail."
    },
    search: {
        destination: "Destination",
        destinationPlaceholder: "Où souhaitez-vous séjourner ?",
        typeCity: "Saisissez une ville…",
        ourDestinations: "Nos destinations",
        noMatches: "Aucun résultat. Veuillez choisir l'une de nos cinq destinations.",
        checkIn: "Arrivée",
        checkOut: "Départ",
        selectDate: "Choisir une date",
        guests: "Voyageurs",
        adults: "Adultes",
        adultsHint: "À partir de 13 ans",
        children: "Enfants",
        childrenHint: "De 0 à 12 ans",
        increase: "Ajouter",
        decrease: "Retirer",
        button: "Rechercher",
        invalidCity: "Désolé, nous proposons uniquement des séjours à Djerba, Zarzis, Hammamet, Tunis et Sousse.",
        checkoutAfter: "Désolé, la date de départ doit être postérieure à la date d'arrivée.",
        pastDate: "Désolé, cette date est déjà passée.",
        prevMonth: "Mois précédent",
        nextMonth: "Mois suivant",
        done: "Terminé"
    },
    results: {
        title: "Villas à {city}",
        count: pl({ zero: "Aucune villa disponible", one: "{n} villa disponible", other: "{n} villas disponibles" }),
        none: "Désolé, aucune villa n'est disponible pour vos dates.",
        noneHint: "Essayez d'autres dates ou une autre destination.",
        totalFor: "Total pour {nights}",
        perNight: "env. {price} par nuit",
        extrasIncluded: "dont {amount} de frais supplémentaires",
        details: "Voir plus de détails",
        invalidSearch: "Veuillez vérifier les informations de votre recherche.",
        photoPrev: "Photo précédente",
        photoNext: "Photo suivante",
        noPhoto: "Photos bientôt disponibles"
    },
    villa: {
        back: "Retour aux résultats",
        about: "À propos de cette villa",
        amenities: "Équipements",
        address: "Adresse",
        yourStay: "Votre séjour",
        nightsLine: "{nights}, en moyenne {price} par nuit",
        extras: "Frais supplémentaires",
        total: "Prix total",
        bookNow: "Réserver maintenant",
        chooseDates: "Choisissez vos dates de séjour pour voir le prix total et réserver.",
        checkAvailability: "Vérifier la disponibilité",
        unavailable: "Désolé, cette villa n'est pas disponible pour les dates choisies.",
        tooManyGuests: "Cette villa peut accueillir jusqu'à {n} voyageurs.",
        descriptionMissing: "Une description détaillée suivra prochainement."
    },
    extraTypes: {
        per_stay: "par séjour",
        per_night: "par nuit",
        per_guest: "par voyageur",
        per_guest_night: "par voyageur et par nuit"
    },
    booking: {
        stepSummary: "Récapitulatif",
        stepPayment: "Paiement",
        stepDetails: "Confirmation",
        stepDone: "Reçu",
        summaryTitle: "Vérifiez votre réservation",
        villa: "Villa",
        location: "Emplacement",
        checkIn: "Arrivée",
        checkOut: "Départ",
        duration: "Durée",
        guests: "Voyageurs",
        total: "Prix total",
        priceDetails: "Détail du prix",
        termsPrefix: "J'ai lu et j'accepte les",
        termsLink: "conditions de réservation",
        termsSuffix: ".",
        toPayment: "Continuer vers le paiement",
        amountTitle: "Montant à envoyer",
        amountHint: "Veuillez envoyer exactement ce montant au destinataire ci-dessous.",
        recipientTitle: "Votre bénéficiaire",
        officialName: "Nom officiel",
        nameForPayment: "Nom pour le paiement",
        readyToCopy: "Prêt à copier",
        copyName: "Copier le nom",
        copied: "Nom copié ! Vous pouvez maintenant le coller sur Western Union.",
        copyFailed: "La copie n'est pas possible sur cet appareil. Veuillez saisir le nom exactement comme indiqué.",
        thanksTitle: "Merci pour votre confiance !",
        thanksText: "Pour finaliser votre réservation, copiez d'abord le nom affiché ci-dessus. Cliquez ensuite sur le lien vers le site officiel de Western Union.",
        wuButton: "Site officiel de Western Union – cliquez ici",
        wuNote: "S'ouvre dans un nouvel onglet. Votre page STAYVORA reste ouverte.",
        checkNote: "Veuillez vérifier le bénéficiaire et le montant avant de payer.",
        guideTitle: "Guide de paiement – Western Union",
        guide: [
            "Ouvrez le site officiel ou l'application Western Union.",
            "Connectez-vous ou créez un compte.",
            "Sélectionnez la Tunisie comme pays de destination.",
            "Saisissez le montant de la réservation.",
            "Choisissez « Retrait en espèces » (Cash pickup).",
            "Saisissez exactement le nom du bénéficiaire affiché ci-dessus.",
            "Vérifiez toutes les informations et confirmez le transfert.",
            "Conservez le reçu du transfert et le numéro MTCN."
        ],
        warning: "Veuillez ne pas fermer cette page STAYVORA pendant que vous effectuez votre transfert.",
        paymentDone: "PAIEMENT EFFECTUÉ",
        formTitle: "Confirmez votre paiement",
        formSubtitle: "Veuillez saisir les informations de votre transfert Western Union. Tous les champs sont obligatoires.",
        mtcn: "Numéro MTCN",
        mtcnHint: "Le numéro de transfert à 10 chiffres figurant sur votre reçu Western Union.",
        mtcnRepeat: "Répéter le numéro MTCN",
        screenshot: "Capture d'écran originale du paiement",
        screenshotHint: "Capture d'écran ou photo de votre reçu Western Union (JPG, PNG, WEBP ou PDF, 8 Mo maximum).",
        chooseFile: "Choisir un fichier",
        changeFile: "Choisir un autre fichier",
        contactTitle: "Vos coordonnées",
        submit: "Envoyer les informations de paiement",
        doneTitle: "Merci ! Nous avons bien reçu vos informations de paiement.",
        doneWithin: "Notre équipe vérifie maintenant votre paiement. Pendant nos heures d'ouverture ({from} à {to}), vous recevrez une réponse dans un délai d'une heure maximum.",
        doneOutside: "Nous sommes actuellement en dehors de nos heures d'ouverture ({from} à {to}). Notre équipe vérifiera votre paiement pendant les prochaines heures d'ouverture.",
        doneNote: "Attention : cet accusé de réception n'est pas encore une confirmation de paiement. Dès que nous aurons vérifié votre paiement, vous recevrez votre confirmation de réservation avec votre numéro de réservation par SMS et par e-mail.",
        doneMessages: "Nous vous avons également envoyé un accusé de réception par SMS et par e-mail.",
        backHome: "Retour à l'accueil",
        leaveWarning: "Votre réservation n'est pas encore terminée. Voulez-vous vraiment quitter cette page ?",
        unavailableTitle: "Ce séjour n'est pas disponible",
        backToSearch: "Retour à la recherche"
    },
    profile: {
        title: "Mon profil",
        subtitle: "Vos informations personnelles en un coup d'œil. Vous pouvez les modifier à tout moment.",
        personal: "Informations personnelles",
        signInMethod: "Méthode de connexion",
        methods: {
            email: "E-mail et mot de passe",
            google: "Google",
            facebook: "Facebook",
            apple: "Apple"
        },
        emailFixed: "Votre adresse e-mail sert à la connexion et ne peut pas être modifiée ici.",
        language: "Langue",
        languageHint: "Les pages, e-mails et SMS vous sont envoyés dans cette langue.",
        saved: "Vos informations ont été enregistrées.",
        passwordTitle: "Changer le mot de passe",
        currentPassword: "Mot de passe actuel",
        newPassword: "Nouveau mot de passe",
        confirmPassword: "Répéter le nouveau mot de passe",
        changePassword: "Changer le mot de passe",
        passwordSaved: "Votre mot de passe a été modifié.",
        passwordManaged: "Vous vous connectez avec {provider}. Votre mot de passe est géré par {provider}.",
        memberSince: "Membre depuis le {date}"
    },
    bookings: {
        title: "Mes réservations",
        subtitle: "Retrouvez ici les réservations que notre équipe a validées pour vous.",
        empty: "Aucune réservation à afficher pour le moment.",
        emptyHint: "Dès que notre équipe aura validé votre réservation, elle apparaîtra ici.",
        reference: "Numéro de réservation",
        referencePending: "Attribué après confirmation",
        submittedOn: "Envoyée le {date}",
        status: {
            new: "Paiement reçu",
            reviewing: "En cours de vérification",
            confirmed: "Confirmée",
            rejected: "Non confirmée"
        },
        searchCta: "Rechercher une villa"
    },
    contact: {
        title: "Coordonnées",
        subtitle: "Nous sommes heureux de vous aider, en français, anglais, allemand et arabe.",
        phone: "Téléphone",
        email: "E-mail",
        person: "Interlocuteur",
        address: "Adresse professionnelle",
        hours: "Heures d'ouverture",
        hoursValue: "Tous les jours de {from} à {to} (heure de Tunisie)",
        landmark: "À côté de la mosquée {name}",
        country: "Tunisie"
    },
    notFound: {
        title: "Page introuvable",
        text: "Désolé, nous n'avons pas trouvé cette page.",
        back: "Retour à l'accueil"
    },
    calendar: {
        monthsShort: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
        monthsLong: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
        weekdays: ["lu", "ma", "me", "je", "ve", "sa", "di"],
        datePattern: "{d} {mon} {y}"
    },
    amenities: {
        private_pool: "Piscine privée",
        pool: "Piscine",
        sea_view: "Vue sur mer",
        beach_access: "Proche de la plage",
        wifi: "Wi-Fi",
        air_conditioning: "Climatisation",
        heating: "Chauffage",
        kitchen: "Cuisine entièrement équipée",
        washing_machine: "Lave-linge",
        tv: "Télévision",
        parking: "Parking",
        garden: "Jardin",
        terrace: "Terrasse",
        bbq: "Barbecue",
        jacuzzi: "Jacuzzi",
        family_friendly: "Adapté aux familles",
        pets_allowed: "Animaux acceptés",
        security: "Service de sécurité",
        cleaning: "Service de ménage",
        enclosed: "Propriété clôturée"
    }
}

const ar: Messages = {
    meta: {
        description: "STAYVORA منصة حجز مغلقة لفلل وشقق خاصة في جربة وجرجيس والحمامات وتونس وسوسة."
    },
    common: {
        continue: "متابعة",
        back: "رجوع",
        cancel: "إلغاء",
        save: "حفظ التغييرات",
        saving: "جارٍ الحفظ…",
        sending: "جارٍ الإرسال…",
        loading: "جارٍ التحميل…",
        close: "إغلاق",
        or: "أو",
        edit: "تعديل",
        securityNote: "محمي بفحص أمني تلقائي.",
        nights: pl({ zero: "{n} ليلة", one: "ليلة واحدة", two: "ليلتان", few: "{n} ليالٍ", many: "{n} ليلة", other: "{n} ليلة" }),
        adultsCount: pl({ other: "البالغون: {n}" }),
        childrenCount: pl({ other: "الأطفال: {n}" }),
        guestsCount: pl({ other: "عدد الضيوف: {n}" }),
        bedrooms: pl({ other: "غرف النوم: {n}" }),
        bathrooms: pl({ other: "الحمّامات: {n}" }),
        upToGuests: "حتى {n} ضيوف"
    },
    errors: {
        required: "عذرًا، يرجى ملء هذا الحقل.",
        invalidEmail: "يرجى إدخال بريد إلكتروني صحيح.",
        invalidName: "يرجى إدخال اسم صحيح.",
        invalidNationality: "يرجى اختيار جنسيتك.",
        phoneCountry: "يرجى اختيار رمز الدولة الدولي.",
        invalidPhone: "يرجى إدخال رقم هاتف صحيح مع رمز الدولة الدولي.",
        passwordShort: "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.",
        passwordWeak: "يجب أن تحتوي كلمة المرور على حروف وأرقام.",
        passwordMismatch: "كلمتا المرور غير متطابقتين.",
        emailTaken: "يوجد حساب بهذا البريد الإلكتروني بالفعل. يرجى تسجيل الدخول.",
        invalidCredentials: "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
        socialAccount: "يتم تسجيل الدخول إلى هذا الحساب عبر {provider}. يرجى استخدام زر «المتابعة باستخدام {provider}».",
        rateLimited: "محاولات كثيرة جدًا. يرجى الانتظار بضع دقائق ثم المحاولة مرة أخرى.",
        captchaFailed: "فشل الفحص الأمني. يرجى المحاولة مرة أخرى.",
        securityPending: "الفحص الأمني ما زال جاريًا. يرجى المحاولة بعد لحظات.",
        providerUnavailable: "تسجيل الدخول عبر {provider} غير متاح بعد. يرجى التسجيل ببريدك الإلكتروني في الوقت الحالي.",
        oauthFailed: "تعذّر إكمال تسجيل الدخول عبر {provider}. يرجى المحاولة مرة أخرى.",
        invalidCode: "رمز الأمان غير صالح أو انتهت صلاحيته.",
        codeAttempts: "عدد كبير من المحاولات الخاطئة. يرجى طلب رمز جديد.",
        wrongPassword: "كلمة المرور الحالية غير صحيحة.",
        invalidMtcn: "يرجى إدخال رقم MTCN المكوّن من 10 أرقام.",
        mtcnMismatch: "رقما MTCN غير متطابقين.",
        screenshotRequired: "يرجى تحميل لقطة شاشة الدفع.",
        screenshotType: "يرجى تحميل صورة (JPG أو PNG أو WEBP) أو ملف PDF.",
        screenshotSize: "حجم الملف كبير جدًا (8 ميغابايت كحد أقصى).",
        termsRequired: "يرجى قبول شروط الحجز للمتابعة.",
        notAvailable: "عذرًا، تم حجز هذه الفيلا للتو في هذه التواريخ. يرجى اختيار تواريخ أخرى.",
        tooManyGuests: "عذرًا، لا تتسع هذه الفيلا لهذا العدد من الضيوف.",
        invalidStay: "يرجى التحقق من تواريخ سفرك.",
        generic: "عذرًا، حدث خطأ ما. يرجى المحاولة مرة أخرى.",
        network: "مشكلة في الاتصال. يرجى التحقق من اتصالك بالإنترنت ثم المحاولة مرة أخرى."
    },
    nav: {
        home: "البحث عن فيلا",
        profile: "ملفي الشخصي",
        bookings: "حجوزاتي",
        privacy: "حماية البيانات",
        about: "من نحن؟",
        contact: "بيانات الاتصال",
        logout: "تسجيل الخروج",
        openMenu: "فتح القائمة",
        closeMenu: "إغلاق القائمة",
        language: "اللغة",
        account: "حسابي"
    },
    footer: {
        tagline: "احجز • أقم • اكتشف",
        rights: "جميع الحقوق محفوظة.",
        terms: "شروط الحجز",
        privacy: "حماية البيانات",
        contact: "اتصل بنا"
    },
    auth: {
        title: "مرحبًا بك في STAYVORA",
        subtitle: "منصة الحجز لدينا مخصصة للضيوف المسجلين. أنشئ حسابك الشخصي أو سجّل الدخول لاكتشاف فللنا وشققنا في تونس.",
        tabRegister: "إنشاء حساب",
        tabLogin: "تسجيل الدخول",
        google: "المتابعة باستخدام Google",
        facebook: "المتابعة باستخدام Facebook",
        apple: "المتابعة باستخدام Apple",
        emailRegister: "التسجيل بالبريد الإلكتروني",
        emailLogin: "تسجيل الدخول بالبريد الإلكتروني",
        socialNote: "مع Google أو Facebook أو Apple تسجّل الدخول مباشرة، دون كلمة مرور منفصلة لـ STAYVORA.",
        firstName: "الاسم",
        lastName: "اللقب",
        nationality: "الجنسية",
        selectNationality: "اختر جنسيتك",
        phone: "رقم الهاتف",
        countryCode: "رمز الدولة",
        selectCountryCode: "الرمز",
        phonePlaceholder: "رقم الجوال",
        email: "البريد الإلكتروني",
        password: "كلمة المرور",
        passwordConfirm: "تأكيد كلمة المرور",
        passwordHint: "8 أحرف على الأقل، تتضمن حروفًا وأرقامًا.",
        showPassword: "إظهار كلمة المرور",
        hidePassword: "إخفاء كلمة المرور",
        submitRegister: "إنشاء حسابي",
        submitLogin: "تسجيل الدخول",
        forgot: "نسيت كلمة المرور؟",
        privacyPrefix: "بإنشاء حساب فإنك توافق على",
        privacyLink: "سياسة الخصوصية",
        privacySuffix: ".",
        languageLabel: "اللغة"
    },
    onboarding: {
        title: "أكمل ملفك الشخصي",
        subtitle: "يرجى إكمال بياناتك الشخصية. نحتاجها للتواصل معك بشأن حجوزاتك.",
        signedInWith: "تم تسجيل الدخول عبر {provider}",
        submit: "حفظ ومتابعة"
    },
    welcome: {
        chooseTitle: "اختر لغتك"
    },
    forgot: {
        title: "إعادة تعيين كلمة المرور",
        subtitle: "أدخل البريد الإلكتروني لحسابك في STAYVORA. سنرسل إليك رمز أمان صالحًا لمرة واحدة.",
        channel: "أين نرسل الرمز؟",
        byEmail: "عبر البريد الإلكتروني",
        bySms: "عبر رسالة نصية إلى رقم هاتفي",
        send: "إرسال رمز الأمان",
        codeTitle: "أدخل رمز الأمان",
        codeText: "إذا كان هناك حساب مرتبط بـ {email}، فقد أرسلنا إليك رمز أمان من 6 أرقام. الرمز صالح لمدة 10 دقائق ويمكن استخدامه مرة واحدة فقط.",
        code: "رمز الأمان",
        newPassword: "كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور الجديدة",
        submit: "حفظ كلمة المرور الجديدة",
        resend: "طلب رمز جديد",
        socialHint: "هل سجّلت عبر Google أو Facebook أو Apple؟ في هذه الحالة يدير مزوّد الخدمة كلمة مرورك. ما عليك سوى تسجيل الدخول بالزر المناسب.",
        backToLogin: "العودة إلى تسجيل الدخول"
    },
    home: {
        eyebrow: "فلل وشقق خاصة في تونس",
        title: "فيلتك الخاصة في تونس",
        subtitle: "فلل وشقق مختارة في جربة وجرجيس والحمامات وتونس وسوسة، تحجزها شخصيًا مع فريق STAYVORA.",
        trust1: "تأكيد شخصي لكل حجز",
        trust2: "خدمة بأربع لغات",
        trust3: "منصة مغلقة للضيوف المسجلين",
        stepsTitle: "كيف يتم الحجز مع STAYVORA",
        step1Title: "ابحث واختر",
        step1Text: "اختر وجهتك وتواريخ سفرك وعدد الضيوف. تظهر لك فقط الفلل المتاحة فعلًا في تواريخك.",
        step2Title: "ادفع عبر Western Union",
        step2Text: "أرسل المبلغ الإجمالي عبر تحويل نقدي من Western Union وأكّد دفعتك برقم MTCN.",
        step3Title: "تأكيد شخصي",
        step3Text: "يتحقق فريقنا من دفعتك ويرسل إليك تأكيد الحجز مع رقم الحجز عبر رسالة نصية والبريد الإلكتروني."
    },
    search: {
        destination: "الوجهة",
        destinationPlaceholder: "أين تودّ الإقامة؟",
        typeCity: "اكتب اسم مدينة…",
        ourDestinations: "وجهاتنا",
        noMatches: "لا توجد نتيجة. يرجى اختيار إحدى وجهاتنا الخمس.",
        checkIn: "تاريخ الوصول",
        checkOut: "تاريخ المغادرة",
        selectDate: "اختر تاريخًا",
        guests: "الضيوف",
        adults: "البالغون",
        adultsHint: "من 13 سنة فما فوق",
        children: "الأطفال",
        childrenHint: "من 0 إلى 12 سنة",
        increase: "إضافة واحد",
        decrease: "إزالة واحد",
        button: "ابحث الآن",
        invalidCity: "عذرًا، نوفّر الإقامة فقط في جربة وجرجيس والحمامات وتونس وسوسة.",
        checkoutAfter: "عذرًا، يجب أن يكون تاريخ المغادرة بعد تاريخ الوصول.",
        pastDate: "عذرًا، هذا التاريخ قد مضى.",
        prevMonth: "الشهر السابق",
        nextMonth: "الشهر التالي",
        done: "تم"
    },
    results: {
        title: "فلل في {city}",
        count: pl({ zero: "لا توجد فيلا متاحة", one: "فيلا واحدة متاحة", two: "فيلتان متاحتان", few: "{n} فلل متاحة", many: "{n} فيلا متاحة", other: "{n} فيلا متاحة" }),
        none: "عذرًا، لا توجد فيلا متاحة في تواريخك.",
        noneHint: "يرجى تجربة تواريخ أخرى أو وجهة أخرى.",
        totalFor: "السعر الإجمالي لـ {nights}",
        perNight: "حوالي {price} لليلة",
        extrasIncluded: "يشمل {amount} تكاليف إضافية",
        details: "عرض المزيد من التفاصيل",
        invalidSearch: "يرجى التحقق من بيانات البحث.",
        photoPrev: "الصورة السابقة",
        photoNext: "الصورة التالية",
        noPhoto: "الصور قريبًا"
    },
    villa: {
        back: "العودة إلى النتائج",
        about: "عن هذه الفيلا",
        amenities: "المرافق",
        address: "العنوان",
        yourStay: "إقامتك",
        nightsLine: "{nights}، بمعدل {price} لليلة",
        extras: "تكاليف إضافية",
        total: "السعر الإجمالي",
        bookNow: "احجز الآن",
        chooseDates: "اختر تواريخ سفرك لرؤية السعر الإجمالي والحجز.",
        checkAvailability: "التحقق من التوفر",
        unavailable: "عذرًا، هذه الفيلا غير متاحة في التواريخ المختارة.",
        tooManyGuests: "تتسع هذه الفيلا لـ {n} ضيوف كحد أقصى.",
        descriptionMissing: "سيتوفر وصف مفصّل قريبًا."
    },
    extraTypes: {
        per_stay: "لكل إقامة",
        per_night: "لكل ليلة",
        per_guest: "لكل ضيف",
        per_guest_night: "لكل ضيف في الليلة"
    },
    booking: {
        stepSummary: "الملخص",
        stepPayment: "الدفع",
        stepDetails: "التأكيد",
        stepDone: "تم الاستلام",
        summaryTitle: "راجع حجزك",
        villa: "الفيلا",
        location: "الموقع",
        checkIn: "الوصول",
        checkOut: "المغادرة",
        duration: "المدة",
        guests: "الضيوف",
        total: "السعر الإجمالي",
        priceDetails: "تفاصيل السعر",
        termsPrefix: "لقد قرأت",
        termsLink: "شروط الحجز",
        termsSuffix: "وأوافق عليها.",
        toPayment: "المتابعة إلى الدفع",
        amountTitle: "المبلغ المطلوب إرساله",
        amountHint: "يرجى إرسال هذا المبلغ بالضبط إلى المستفيد المذكور أدناه.",
        recipientTitle: "المستفيد من الدفع",
        officialName: "الاسم الرسمي",
        nameForPayment: "الاسم الخاص بالدفع",
        readyToCopy: "جاهز للنسخ",
        copyName: "نسخ الاسم",
        copied: "تم نسخ الاسم! يمكنك الآن لصقه في Western Union.",
        copyFailed: "النسخ غير متاح على هذا الجهاز. يرجى كتابة الاسم تمامًا كما هو معروض.",
        thanksTitle: "شكرًا لثقتك!",
        thanksText: "لإتمام حجزك، يرجى أولًا نسخ الاسم المعروض أعلاه. ثم انقر على الرابط المؤدي إلى الموقع الرسمي لـ Western Union.",
        wuButton: "الموقع الرسمي لـ Western Union – انقر هنا",
        wuNote: "يُفتح في علامة تبويب جديدة. تبقى صفحة STAYVORA مفتوحة.",
        checkNote: "يرجى التحقق من المستفيد والمبلغ قبل الدفع.",
        guideTitle: "دليل الدفع – Western Union",
        guide: [
            "افتح الموقع الرسمي لـ Western Union أو التطبيق.",
            "سجّل الدخول أو أنشئ حسابًا.",
            "اختر تونس كبلد الوجهة.",
            "أدخل مبلغ الحجز.",
            "اختر «الاستلام نقدًا» (Cash pickup).",
            "أدخل اسم المستفيد المعروض أعلاه بدقة.",
            "تحقّق من جميع البيانات وأكّد التحويل.",
            "احتفظ بإيصال التحويل ورقم MTCN."
        ],
        warning: "يرجى عدم إغلاق صفحة STAYVORA هذه أثناء إجراء التحويل.",
        paymentDone: "تم الدفع",
        formTitle: "أكّد عملية الدفع",
        formSubtitle: "يرجى إدخال بيانات تحويل Western Union. جميع الحقول إلزامية.",
        mtcn: "رقم MTCN",
        mtcnHint: "رقم التحويل المكوّن من 10 أرقام الموجود على إيصال Western Union.",
        mtcnRepeat: "أعد إدخال رقم MTCN",
        screenshot: "لقطة الشاشة الأصلية للدفع",
        screenshotHint: "لقطة شاشة أو صورة لإيصال Western Union (JPG أو PNG أو WEBP أو PDF، بحد أقصى 8 ميغابايت).",
        chooseFile: "اختر ملفًا",
        changeFile: "اختر ملفًا آخر",
        contactTitle: "بيانات الاتصال الخاصة بك",
        submit: "إرسال بيانات الدفع",
        doneTitle: "شكرًا لك! لقد استلمنا بيانات الدفع الخاصة بك.",
        doneWithin: "يقوم فريقنا الآن بالتحقق من دفعتك. خلال ساعات العمل (من {from} إلى {to}) ستتلقى ردًا خلال ساعة واحدة كحد أقصى.",
        doneOutside: "نحن حاليًا خارج ساعات العمل (من {from} إلى {to}). سيتحقق فريقنا من دفعتك خلال ساعات العمل القادمة.",
        doneNote: "يرجى الملاحظة: هذا الإشعار بالاستلام ليس تأكيدًا للدفع بعد. بمجرد التحقق من دفعتك، ستتلقى تأكيد الحجز مع رقم الحجز عبر رسالة نصية والبريد الإلكتروني.",
        doneMessages: "لقد أرسلنا إليك أيضًا إشعارًا بالاستلام عبر رسالة نصية والبريد الإلكتروني.",
        backHome: "العودة إلى الصفحة الرئيسية",
        leaveWarning: "لم يكتمل حجزك بعد. هل تريد حقًا مغادرة هذه الصفحة؟",
        unavailableTitle: "هذه الإقامة غير متاحة",
        backToSearch: "العودة إلى البحث"
    },
    profile: {
        title: "ملفي الشخصي",
        subtitle: "بياناتك الشخصية في لمحة. يمكنك تعديلها في أي وقت.",
        personal: "البيانات الشخصية",
        signInMethod: "طريقة تسجيل الدخول",
        methods: {
            email: "البريد الإلكتروني وكلمة المرور",
            google: "Google",
            facebook: "Facebook",
            apple: "Apple"
        },
        emailFixed: "يُستخدم بريدك الإلكتروني لتسجيل الدخول ولا يمكن تغييره هنا.",
        language: "اللغة",
        languageHint: "ستظهر الصفحات والرسائل الإلكترونية والنصية بهذه اللغة.",
        saved: "تم حفظ بياناتك.",
        passwordTitle: "تغيير كلمة المرور",
        currentPassword: "كلمة المرور الحالية",
        newPassword: "كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور الجديدة",
        changePassword: "تغيير كلمة المرور",
        passwordSaved: "تم تغيير كلمة المرور.",
        passwordManaged: "تسجّل الدخول عبر {provider}. تتم إدارة كلمة مرورك من قِبل {provider}.",
        memberSince: "عضو منذ {date}"
    },
    bookings: {
        title: "حجوزاتي",
        subtitle: "هنا تجد الحجوزات التي أتاحها لك فريقنا.",
        empty: "لا توجد حجوزات لعرضها بعد.",
        emptyHint: "بمجرد أن يتيح فريقنا حجزك، سيظهر هنا.",
        reference: "رقم الحجز",
        referencePending: "يُمنح بعد التأكيد",
        submittedOn: "أُرسل بتاريخ {date}",
        status: {
            new: "تم استلام الدفع",
            reviewing: "قيد المراجعة",
            confirmed: "مؤكَّد",
            rejected: "غير مؤكَّد"
        },
        searchCta: "البحث عن فيلا"
    },
    contact: {
        title: "بيانات الاتصال",
        subtitle: "يسعدنا مساعدتك بالعربية والفرنسية والإنجليزية والألمانية.",
        phone: "الهاتف",
        email: "البريد الإلكتروني",
        person: "الشخص المسؤول",
        address: "عنوان الشركة",
        hours: "ساعات العمل",
        hoursValue: "يوميًا من {from} إلى {to} (بتوقيت تونس)",
        landmark: "بجانب جامع {name}",
        country: "تونس"
    },
    notFound: {
        title: "الصفحة غير موجودة",
        text: "عذرًا، لم نتمكن من العثور على هذه الصفحة.",
        back: "العودة إلى الصفحة الرئيسية"
    },
    calendar: {
        monthsShort: ["جانفي", "فيفري", "مارس", "أفريل", "ماي", "جوان", "جويلية", "أوت", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
        monthsLong: ["جانفي", "فيفري", "مارس", "أفريل", "ماي", "جوان", "جويلية", "أوت", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
        weekdays: ["ن", "ث", "ر", "خ", "ج", "س", "ح"],
        datePattern: "{d} {mon} {y}"
    },
    amenities: {
        private_pool: "مسبح خاص",
        pool: "مسبح",
        sea_view: "إطلالة على البحر",
        beach_access: "قريب من الشاطئ",
        wifi: "واي فاي",
        air_conditioning: "تكييف",
        heating: "تدفئة",
        kitchen: "مطبخ مجهّز بالكامل",
        washing_machine: "غسالة ملابس",
        tv: "تلفاز",
        parking: "موقف سيارات",
        garden: "حديقة",
        terrace: "شرفة",
        bbq: "شواء",
        jacuzzi: "جاكوزي",
        family_friendly: "مناسب للعائلات",
        pets_allowed: "يُسمح بالحيوانات الأليفة",
        security: "خدمة حراسة",
        cleaning: "خدمة تنظيف",
        enclosed: "عقار مسيّج"
    }
}

export const dictionaries: Record<Locale, Messages> = { en, de, fr, ar }
